import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, RefreshCw, Plus, Pencil, Trash2, Lock, Download } from 'lucide-react';
import { chatApi, fmtDate, fmtTry } from './api';
import { Card, Field, Toggle, Modal, Badge, inputCls, btnPrimary, btnGhost } from './ui';

const KINDS = [
  { key: 'page', label: 'Sayfalar' },
  { key: 'policy', label: 'Politikalar' },
  { key: 'faq', label: 'Sık sorulanlar' },
  { key: 'note', label: 'Notlar' },
];
const EMPTY = { kind: 'faq', title: '', content: '', sourceUrl: '', enabled: true, locked: false, priority: 5 };

function CatalogCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (refresh) => {
    setLoading(true);
    try { setData(await chatApi('/catalog', { query: refresh ? { refresh: 1 } : {} })); if (refresh) toast.success('Katalog yenilendi'); }
    catch (e) { toast.error(`Katalog: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);
  const items = data?.items || [];
  return (
    <Card
      title="Ürün kataloğu"
      hint={data ? `${data.count} ürün · son çekim ${fmtDate(data.fetchedAt)}` : 'Yükleniyor'}
      actions={<button className={btnGhost} disabled={loading} onClick={() => load(true)}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Yenile</button>}
    >
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-[11px] text-gray-500 text-left sticky top-0 bg-white"><tr><th className="py-1 pr-2">Ürün</th><th className="px-2">Fiyat</th><th className="px-2">Stok</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-gray-100">
                <td className="py-1.5 pr-2"><div className="flex items-center gap-2">
                  {p.image ? <img src={p.image} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                  <span className="line-clamp-2">{p.title}</span>
                </div></td>
                <td className="px-2 whitespace-nowrap">{fmtTry(p.price)}{p.oldPrice ? <span className="ml-1 text-xs text-gray-400 line-through">{fmtTry(p.oldPrice)}</span> : null}</td>
                <td className="px-2">{p.inStock ? <Badge tone="green">Var</Badge> : <Badge tone="red">Yok</Badge>}</td>
              </tr>
            ))}
            {!loading && !items.length && <tr><td colSpan={3} className="py-4 text-center text-gray-400">Katalog boş</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function KnowledgeTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await chatApi('/knowledge')).items || []); }
    catch (e) { toast.error(`Bilgi kaynakları: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...body } = editing;
      const payload = { kind: body.kind, title: body.title, content: body.content, sourceUrl: body.sourceUrl || undefined, enabled: body.enabled, locked: body.locked, priority: Number(body.priority) };
      if (id) await chatApi(`/knowledge/${id}`, { method: 'PUT', body: payload });
      else await chatApi('/knowledge', { method: 'POST', body: payload });
      setEditing(null);
      toast.success('Kaydedildi');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm(`"${it.title}" silinsin mi?`)) return;
    try { await chatApi(`/knowledge/${it.id}`, { method: 'DELETE' }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const toggle = async (it) => {
    try { await chatApi(`/knowledge/${it.id}`, { method: 'PUT', body: { enabled: !it.enabled } }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const sync = async () => {
    setSyncing(true); setSyncResult(null);
    try { setSyncResult(await chatApi('/knowledge/sync', { method: 'POST', body: {} })); load(); }
    catch (e) { toast.error(`Senkron: ${e.message}`); }
    finally { setSyncing(false); }
  };

  return (
    <>
      <CatalogCard />
      <Card
        title="Bilgi kaynakları"
        hint="Asistan bu metinlerden yanıt verir. Kilitli kayıtlar siteden çekmede ezilmez."
        actions={(
          <div className="flex gap-1.5 shrink-0">
            <button className={btnGhost} disabled={syncing} onClick={sync} title="Sayfaları siteden çek">{syncing ? <Loader2 className="animate-spin" /> : <Download />}Siteden çek</button>
            <button className={btnPrimary} onClick={() => setEditing({ ...EMPTY })}><Plus className="w-4 h-4" />Yeni</button>
          </div>
        )}
      >
        {syncResult && (
          <div className="mb-3 rounded-lg bg-indigo-50 text-indigo-800 text-xs p-2.5 space-y-0.5">
            <div>{syncResult.totalPages} sayfadan {syncResult.stored} kaydedildi, {syncResult.skippedLocked} kilitli atlandı.</div>
            {syncResult.empty?.length > 0 && <div>İçeriği boş: {syncResult.empty.join(', ')}</div>}
            {syncResult.failed?.length > 0 && <div className="text-red-700">Hata: {syncResult.failed.map((f) => (typeof f === 'string' ? f : f.url || JSON.stringify(f))).join(', ')}</div>}
          </div>
        )}
        {loading && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
        {!loading && !items.length && <p className="text-sm text-gray-400">Henüz kayıt yok.</p>}
        {KINDS.map((k) => {
          const list = items.filter((i) => i.kind === k.key);
          if (!list.length) return null;
          return (
            <div key={k.key} className="mb-4">
              <div className="text-[11px] font-medium text-gray-400 mb-1">{k.label} ({list.length})</div>
              {list.map((it) => (
                <div key={it.id} className="flex items-center gap-2 border-t border-gray-100 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-800 truncate flex items-center gap-1.5">
                      {it.locked && <Lock className="w-3 h-3 text-gray-400" />}{it.title}
                    </div>
                    <div className="text-[11px] text-gray-400 truncate">{it.content}</div>
                  </div>
                  <Badge tone="gray">Öncelik {it.priority}</Badge>
                  <button type="button" onClick={() => toggle(it)} title="Aç/kapat">{it.enabled ? <Badge tone="green">Açık</Badge> : <Badge tone="amber">Kapalı</Badge>}</button>
                  <button className="p-1 text-gray-400 hover:text-gray-700" onClick={() => setEditing({ ...EMPTY, ...it, sourceUrl: it.sourceUrl || '' })}><Pencil className="w-3.5 h-3.5" /></button>
                  <button className="p-1 text-gray-300 hover:text-red-600" onClick={() => remove(it)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          );
        })}
      </Card>
      {editing && (
        <Modal
          wide
          title={editing.id ? 'Kaydı düzenle' : 'Yeni kayıt'}
          onClose={() => setEditing(null)}
          footer={<><button className={btnGhost} onClick={() => setEditing(null)}>Vazgeç</button><button className={btnPrimary} disabled={saving || !editing.title.trim() || !editing.content.trim()} onClick={save}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Kaydet</button></>}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tür">
              <select className={inputCls} value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Öncelik (0-10)"><input type="number" min={0} max={10} className={inputCls} value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: Math.max(0, Math.min(10, parseInt(e.target.value, 10) || 0)) })} /></Field>
          </div>
          <Field label="Başlık"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
          <Field label="İçerik"><textarea rows={8} className={inputCls} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} /></Field>
          <Field label="Kaynak URL"><input className={inputCls} placeholder="https://..." value={editing.sourceUrl} onChange={(e) => setEditing({ ...editing, sourceUrl: e.target.value })} /></Field>
          <Toggle label="Açık" checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} />
          <Toggle label="Kilitli (siteden çekme ezmez)" checked={editing.locked} onChange={(v) => setEditing({ ...editing, locked: v })} />
        </Modal>
      )}
    </>
  );
}
